import { z } from 'zod';

/**
 * Die dynamische Konfiguration der zentralen Edge.
 *
 * ## Warum das ein Vertrag ist und keine beiläufige JSON-Antwort
 *
 * Traefik pollt `GET /internal/edge/config` und übernimmt, was dort steht. Diese
 * Antwort **ist** der gesamte Load-Balancer: Steht eine Domain nicht darin, ist
 * die Website nicht erreichbar; steht ein falsches Ziel darin, landet der
 * Verkehr eines Kunden bei einem anderen. Ein Vertrag, gegen den der Master
 * seine eigene Ausgabe prüft, bevor er sie ausliefert, ist deshalb hier
 * angebracht — nicht bloß eine Typannotation.
 *
 * Das Schema bildet **nur ab, was wir tatsächlich erzeugen**, nicht Traefiks
 * gesamte Oberfläche. Was hier nicht steht, kann der Master nicht ausliefern —
 * dieselbe Haltung wie bei `siteSpecSchema` gegenüber dem Docker-Socket.
 *
 * ## Die Eigenschaft, um die es in diesem Meilenstein geht
 *
 * `passHostHeader` steht ausdrücklich in jedem Dienst, obwohl `true` Traefiks
 * Vorgabe ist. Ohne den unveränderten Host-Header sähe ein Container nie seine
 * echte Domain — und WordPress baut daraus absolute URLs, Cookies und
 * Weiterleitungen. Eine Vorgabe, die stillschweigend kippen kann, ist für eine
 * tragende Eigenschaft die falsche Absicherung.
 *
 * ## Und die, um die es nicht geht
 *
 * Der Master steht **nicht** im Datenpfad. Fällt er aus, behält Traefik seine
 * zuletzt geholte Konfiguration und liefert weiter aus. Diese Datei beschreibt
 * deshalb einen Zustand, keinen Befehl: Es gibt kein Feld, das etwas anstößt.
 */

/**
 * Name eines Routers, Dienstes oder einer Middleware.
 *
 * Der Zeichenvorrat ist eng gehalten, weil diese Namen Schlüssel in Traefiks
 * Konfigurationsbaum sind und in dessen Regelsprache referenziert werden.
 */
export const edgeObjectNameSchema = z.string().regex(/^[a-z\d][a-z\d-]{0,511}$/, {
  message:
    'Muss aus Kleinbuchstaben, Ziffern und Bindestrichen bestehen und mit einem Buchstaben oder einer Ziffer beginnen.',
});

/** Die Einstiegspunkte, die die Edge kennt. Siehe `infra/edge/docker-compose.yml`. */
export const EDGE_ENTRYPOINTS = {
  /** Port 80 — nur für die ACME-Prüfung und die Umleitung auf HTTPS. */
  web: 'web',
  /** Port 443 — hier läuft der Kundenverkehr. */
  websecure: 'websecure',
} as const;

export type EdgeEntrypoint = (typeof EDGE_ENTRYPOINTS)[keyof typeof EDGE_ENTRYPOINTS];

/**
 * Namensschema der SFTP-Einstiegspunkte: `sftp1`, `sftp2`, … `sftpN`.
 *
 * Einer je Worker, und zwar **statisch** in `infra/edge/docker-compose.yml`
 * angelegt — Traefiks Einstiegspunkte lassen sich nicht dynamisch nachreichen.
 * Aus dem HTTP-Provider kommen nur die Router und Dienste, die auf sie zeigen.
 * Deshalb steht dort ein Vorrat (Vorgabe zehn), und ein neuer Worker kostet
 * keinen Neustart der Edge, solange der Vorrat reicht.
 */
export function buildSftpEntrypointName(ordinal: number): string {
  return `sftp${String(ordinal)}`;
}

/**
 * Der Port, den ein Kunde in sein SFTP-Programm einträgt.
 *
 * `basePort + ordinal - 1`. Der erste Worker bekommt den Basisport, jeder
 * weitere den nächsten. Diese eine Zeile ist der Preis dafür, dass SSH kein SNI
 * kennt — und der Grund, warum die Zugangsdaten zweier Kunden auf verschiedenen
 * Workern nicht identisch sind.
 *
 * Eine Funktion und keine drei Rechnungen im Code: Master und Renderer müssen
 * denselben Wert bilden, und ein Abweichen um eins wäre der Zugang zum falschen
 * Server.
 */
export function buildSftpPort(basePort: number, ordinal: number): number {
  return basePort + ordinal - 1;
}

/** Name des ACME-Ausstellers in der statischen Konfiguration der Edge. */
export const EDGE_CERT_RESOLVER = 'letsencrypt';

export const edgeTlsSchema = z.object({
  /**
   * Wer das Zertifikat besorgt.
   *
   * **Optional, und das ist der Kern der Zertifikatsstrategie.** Genau ein
   * Router je Kundendomain trägt den Aussteller — der des Apex, mit dem
   * `www`-Namen als SAN. Der Router für `www` bekommt bewusst *keinen*:
   * Traefik leitet die zu bestellenden Namen sonst aus der Router-Regel ab und
   * stellte ein zweites Zertifikat für denselben Kunden aus. Das wäre nicht
   * bloß Verschwendung, sondern der doppelte Verbrauch eines Kontingents, das
   * bei 50 Zertifikaten je registrierter Domain und Woche endet.
   *
   * Ohne Aussteller wählt Traefik aus seinem Speicher das Zertifikat, das zur
   * SNI passt — und das ist genau das des Apex, weil `www` dort als SAN steht.
   */
  certResolver: z.literal(EDGE_CERT_RESOLVER).optional(),
  /**
   * Die Namen, für die ein Zertifikat bestellt wird.
   *
   * Hier stehen **ausschließlich verifizierte** Namen. Eine einzige
   * unerreichbare SAN lässt die gesamte ACME-Bestellung scheitern — auch für den
   * Apex, der längst zeigt. Ein fehlender `www`-Record darf den Kunden nicht
   * seine Hauptdomain kosten.
   */
  domains: z
    .array(
      z.object({
        main: z.string(),
        sans: z.array(z.string()).optional(),
      }),
    )
    .optional(),
});

export const edgeRouterSchema = z.object({
  /** Traefiks Regelsprache, hier immer genau ein ``Host(`…`)``. */
  rule: z.string(),
  entryPoints: z.array(z.enum([EDGE_ENTRYPOINTS.web, EDGE_ENTRYPOINTS.websecure])).min(1),
  service: edgeObjectNameSchema,
  middlewares: z.array(edgeObjectNameSchema).optional(),
  tls: edgeTlsSchema.optional(),
});

export const edgeServiceSchema = z.object({
  loadBalancer: z.object({
    servers: z.array(z.object({ url: z.string().url() })).min(1),
    /** Siehe den Kopf dieser Datei. Der Grund für die gesamte Edge-Schicht. */
    passHostHeader: z.literal(true),
  }),
});

export const edgeMiddlewareSchema = z.object({
  /** Die 301 von `www` auf den Apex. Kanonisch ist der Apex. */
  redirectRegex: z.object({
    regex: z.string(),
    replacement: z.string(),
    permanent: z.literal(true),
  }),
});

// -- TCP: der SFTP-Weg (M4c) -------------------------------------------------

/**
 * Die Regel eines TCP-Routers — immer ``HostSNI(`*`)``, als Literal.
 *
 * SSH kennt kein SNI. Die Edge kann an einer eingehenden SFTP-Verbindung also
 * gar nicht erkennen, zu welchem Worker sie gehört; sie unterscheidet
 * ausschließlich am **Einstiegspunkt**, und der hängt an der laufenden Nummer
 * des Workers. ``HostSNI(`*`)`` ist der von Traefik dafür vorgesehene Weg für
 * Nicht-TLS-Protokolle.
 *
 * Als Literal und nicht als freie Zeichenkette: Die Regel ist keine Entscheidung
 * mehr, die irgendwo getroffen wird, sondern eine Eigenschaft dieses Vertrags.
 * Ein Router mit einer anderen Regel könnte an dieser Stelle nur ein Versehen
 * sein.
 */
export const EDGE_TCP_RULE = 'HostSNI(`*`)';

export const edgeTcpRouterSchema = z.object({
  rule: z.literal(EDGE_TCP_RULE),
  /**
   * Genau **ein** Einstiegspunkt je Router.
   *
   * Zwei wären zwei Worker hinter demselben Port — und damit ein Kunde, der
   * mal auf seinen Dateien landet und mal auf denen eines anderen Servers.
   */
  entryPoints: z.array(edgeObjectNameSchema).length(1),
  service: edgeObjectNameSchema,
});

export const edgeTcpServiceSchema = z.object({
  loadBalancer: z.object({
    /**
     * `host:port` des Workers im privaten Netz.
     *
     * Genau eine Adresse: Ein zweiter Eintrag verteilte die Verbindungen eines
     * Kunden auf zwei Server, und seine Dateien liegen nur auf einem davon.
     */
    servers: z.array(z.object({ address: z.string() })).length(1),
    /**
     * Das PROXY-Protokoll zum Worker, seit M6b.
     *
     * ## Warum es das braucht
     *
     * SSH kennt kein `X-Forwarded-For`. Ohne diese Zeile sieht der SFTP-Dienst
     * des Workers als Quelladresse **immer die Edge** — bei jeder Verbindung
     * jedes Kunden. Eine Sperre nach Fehlversuchen zählte damit die
     * Fehlversuche aller Kunden zusammen und sperrte nach kurzer Zeit die Edge
     * aus. Das ist nicht „ein Kunde kommt nicht mehr rein", sondern „kein Kunde
     * kommt mehr rein".
     *
     * Version 2 und nicht 1: Das Binärformat ist eindeutig zu erkennen. Ein
     * Dienst, der es nicht erwartet, verwirft die Verbindung, statt die ersten
     * Bytes als Nutzdaten zu lesen — ein lauter Fehler statt eines stillen.
     *
     * Optional, weil eine Edge im Stand vor M6b das Feld nicht kennt und eine
     * Konfiguration ohne es weiterhin gültig ist.
     */
    proxyProtocol: z.object({ version: z.union([z.literal(1), z.literal(2)]) }).optional(),
  }),
});

export const edgeConfigSchema = z.object({
  http: z.object({
    routers: z.record(edgeObjectNameSchema, edgeRouterSchema),
    services: z.record(edgeObjectNameSchema, edgeServiceSchema),
    middlewares: z.record(edgeObjectNameSchema, edgeMiddlewareSchema),
  }),
  /**
   * Der SFTP-Weg, seit M4c.
   *
   * Steht neben `http` und nicht darin, weil Traefik beide Ebenen getrennt
   * führt: Ein TCP-Router sieht keinen Host-Header, und ein HTTP-Router sieht
   * keine rohe Verbindung.
   */
  tcp: z.object({
    routers: z.record(edgeObjectNameSchema, edgeTcpRouterSchema),
    services: z.record(edgeObjectNameSchema, edgeTcpServiceSchema),
  }),
});

export type EdgeTls = z.infer<typeof edgeTlsSchema>;
export type EdgeRouter = z.infer<typeof edgeRouterSchema>;
export type EdgeService = z.infer<typeof edgeServiceSchema>;
export type EdgeMiddleware = z.infer<typeof edgeMiddlewareSchema>;
export type EdgeTcpRouter = z.infer<typeof edgeTcpRouterSchema>;
export type EdgeTcpService = z.infer<typeof edgeTcpServiceSchema>;
export type EdgeConfig = z.infer<typeof edgeConfigSchema>;

/** Header, über den sich die Edge beim Master ausweist. */
export const EDGE_CONFIG_HEADER = 'x-edge-token';

/** Pfad des Endpunkts unterhalb von `INTERNAL_API_PREFIX`. */
export const EDGE_CONFIG_PATH = 'edge/config';
